
// Vue
import {
	defineComponent,
	PropType,
	ref,
	computed
} from 'vue'

// Router
import { useRouter, type Router } from 'vue2-helpers/vue-router'

// Components
import PrimaryButton from '@/components/Buttons/PrimaryButton.vue'
import FormattedPrice from '@/components/FormattedPrice.vue'

// Types
import { LocalEventTicket, TicketOrder } from '@/types/entitiesTypes'
import { AddMainErrorAction } from '@/types/storeTypes'
import { CatchError } from '@/types/commonTypes'
import { OrderResponse } from '@/types/apiTypes'
import { AxiosError } from 'axios'

type OrderItemRequestMethod = () => Promise<OrderResponse | undefined>

// Utils
import { formatNumber, getValidationCode } from '@/utils/helpers'

// Api
import eventApi from '@/api/events'
import TicketingInfoModal from '@/components/modals/Ticketing/TicketingInfoModal.vue'

// Vuex
import { createVuexHelpers } from 'vue2-helpers'
import {
	ValidationMessageEnum,
	RESERVATION_TICKET_VALIDATION_CODES_TS,
	PURCHASE_TICKET_VALIDATION_CODES_TS,
	TICKETS_QUANTITY_UNAVAILABLE
} from '@/types/constantsTypes'

export default defineComponent({
	name: 'TicketingPaymentFooter',

	emits: [
		'handle-pay',
		'change-ticket-order',
		'show-unavailable-popup',
		'check-quantity-availability'
	],

	components: {
		FormattedPrice,
		TicketingInfoModal,
		PrimaryButton
	},

	props: {
		orderId: {
			type: String,
			default: ''
		},

		disabled: {
			type: Boolean,
			default: false
		},

		purchaseLimitRemaining: {
			type: Number,
			default: null
		},

		eventTiers: {
			type: Array as PropType<LocalEventTicket[]>,
			default: () => []
		},

		amount: {
			type: Number,
			default: 0
		},

		ticketOrderCreditCardId: {
			type: String,
			default: null,
		},

		getOrder: {
			type: Function,
			required: true,
		},

		ticketsCount: {
			type: Number,
			default: 0
		},
	},

	setup(props, { emit }) {
		// Refs
		const isLoading = ref(false)
		const paymentFailed = ref(false)
		const wrongCount = ref(false)
		const serviceError = ref(false)

		// Computed
		const unavailabilityTitle = computed(
			() => `${props.ticketsCount > 1 ? 'Tickets' : 'Ticket'} Purchase Unsuccessful`
		)

		// Router
		const router: Router = useRouter()

		// Vuex
		const { useActions } = createVuexHelpers()
		const { addMainError }: { addMainError: AddMainErrorAction } = useActions('ui', ['addMainError'])

		const payWithoutMagensaModal = async (): Promise<void> => {
			isLoading.value = true

			try {
				await addCountAndAmount()
			} catch (error) {
				isLoading.value = false

				return
			}

			await reserveAndPurchaseTickets()
		}

		// Methods
		const reserveAndPurchaseTickets = async (): Promise<void> => {
			isLoading.value = true

			try {
				await eventApi.reserveTickets(props.orderId)
				// eslint-disable-next-line
			} catch (e: any) {
				const error = e as CatchError
				if (error instanceof AxiosError ) {
					onPurchaseError(error, RESERVATION_TICKET_VALIDATION_CODES_TS)
				}

				return
			}

			try {
				const orderResponse = await eventApi.purchaseTickets(props.orderId)
				const orderId = orderResponse?.data?.eventPurchaseOrderId || ''

				await onPaymentSuccess(orderId)
			} catch (e) {
				const error = e as CatchError
				if (error instanceof AxiosError ) {
					onPurchaseError(error, PURCHASE_TICKET_VALIDATION_CODES_TS)
				}
			}
		}

		// eslint-disable-next-line
		const onPurchaseError = (e: AxiosError, codes: ValidationMessageEnum[]): void => {
			isLoading.value = false
			const validationCode = getValidationCode(e)
			const isWrongCount = TICKETS_QUANTITY_UNAVAILABLE.includes(validationCode)
			const isAccountLimit = validationCode === ValidationMessageEnum.ALL_AVAILABLE_TICKETS_FOR_ACCOUNT

			if (isWrongCount) {
				onWrongTicketsCount(isAccountLimit)

				return
			}

			// Means unavailable event
			if (validationCode === ValidationMessageEnum.EVENT_STATUS_NOT_ACTIVE) {
				emit('show-unavailable-popup')

				return
			}

			if (validationCode === ValidationMessageEnum.TICKET_ORDER_RESERVED) {
				serviceError.value = true

				return
			}

			onPaymentFailed()
		}

		const closeWrongCountModal = (): void => {
			wrongCount.value = false
		}

		const onWrongTicketsCount = (isAccountLimit: boolean): void => {
			if (isAccountLimit) {
				emit('check-quantity-availability')

				return
			}

			wrongCount.value = true
		}

		const onPaymentFailed = (): void => {
			isLoading.value = false
			paymentFailed.value = true
		}

		const closePaymentFailedModal = (): void => {
			paymentFailed.value = false
		}

		const pay = (): void => {
			emit('handle-pay', props.amount ? '' : 'reserve-and-purchase')
		}

		const onPaymentSuccess = async (orderId: string): Promise<void> => {
			if (!orderId) {
				addMainError('Order was not found')

				return
			}

			await router.push({ name: 'order', params: { orderId, } })
		}

		const changeTicketOrder = (ticketOrder: TicketOrder): void => {
			emit('change-ticket-order', ticketOrder)
		}

		const addCountAndAmount = async (): Promise<void> => {
			// Need to get latest data (2 tabs at the same time)
			await props.getOrder()

			const requestsArray: OrderItemRequestMethod[] = []

			props.eventTiers.forEach((tier: LocalEventTicket) => {
				if (!tier.quantity && !tier.orderItemId) return

				if (tier.orderItemId) {
					const data = {
						quantity: tier.quantity,
					}

					requestsArray.push(() => eventApi.updateTicketTierItemInOrder((tier.orderItemId as string), data))
				} else {
					const data = {
						ticketOrderId: props.orderId,
						eventTicketTypeId: tier.id,
						quantity: tier.quantity,
					}

					requestsArray.push(() => eventApi.addTicketTierItemToOrder(data))
				}
			})

			try {
				await Promise.all(requestsArray.map(request => request()))
				await props.getOrder(true)
			} catch (e) {
				const error = e as CatchError
				if (error instanceof AxiosError) {
					props.getOrder(true)
					serviceError.value = true
				}

				throw (error)
			}
		}

		const openCreditCardModal = async (): Promise<void> => {
			try {
				isLoading.value = true

				try {
					await addCountAndAmount()
				} catch (e) {
					isLoading.value = false

					return
				}

				if (props.ticketOrderCreditCardId) {
					await reserveAndPurchaseTickets()
				} else {
					onPaymentFailed()
				}
			} finally {
				isLoading.value = false
			}
		}

		const closeServiceErrorModal = (): void => {
			serviceError.value = false
		}

		return {
			isLoading,
			paymentFailed,
			wrongCount,
			serviceError,
			unavailabilityTitle,
			pay,
			formatNumber,
			openCreditCardModal,
			changeTicketOrder,
			onPaymentSuccess,
			onPaymentFailed,
			closePaymentFailedModal,
			onWrongTicketsCount,
			closeWrongCountModal,
			closeServiceErrorModal,
			reserveAndPurchaseTickets,
			payWithoutMagensaModal,
		}
	}
})
